import { getEddText } from '@/helpers/shipping'
import { getCategoryTreeFromCrumbs } from '@/integrations/_helpers/getCategoryTreeFromCrumbs'
import { setFitmentFilters } from '@/helpers/algolia'
import { getPageType, getPageName } from './helpers'

export default class Segment {
    constructor(ctx) {
        this.$config = ctx.$config
        this.$sitewideData = ctx.$sitewideData
        this.$emailTracker = ctx.$emailTracker
        this.store = ctx.store

        // Create Dependencies
        ctx.$integrationsManager.onReady((integrations) => {
            this.$cognito = integrations.cognito
            this.$geolocation = integrations.geolocation
            this.$search = integrations.search
        })
    }

    // This will get the user id of the currently logged in user, if a user is logged in
    async getUserId() {
        if (this.$cognito?.enabled) {
            const userAttributes = await this.$cognito.getUserAttributes()

            return userAttributes?.id
        }

        return null
    }

    async generateUserId(email) {
        if (!this.$cognito.enabled) return
        return await this.$cognito.generateUserId(email)
    }

    isSalesRep() {
        return !!(this.$cognito?.enabled && this.$cognito.isSalesAgent)
    }

    _isReady() {
        return new Promise((resolve) => {
            window.analytics.ready(resolve)
        })
    }

    async getAnonId() {
        await this._isReady()
        return window.analytics.user().anonymousId()
    }

    async track(name, data) {
        const trackData = {
            ...data,
            isSalesRep: this.isSalesRep(),
            ...this.getGeoLocationData(),
        }

        const email = this.$emailTracker.getEmail()
        if (email) trackData.email = email

        // if userId comes back as non-null then the user
        // is signed in
        const userId = await this.getUserId()
        if (userId) trackData.userId = userId

        window.analytics.track(name, trackData)
    }

    async emitIdentify(data = {}) {
        // We do not want to fire an identify event without a UserId.
        // We are no longer supporting Anonymous IDs
        // Per Chris Wall: We are no longer identifying people unless we know them
        let { email, userId } = data
        // If they did not give us a UserId, fetch it
        if (!userId) userId = await this.getUserId()

        // If we STILL do not have a userId, jump ship
        // Note: this means any sight without Cognito enabled will never run Identify
        // Chris Wall is aware of this issue.
        if (!userId) return

        // If they didn't specify an email, fetch it
        if (!email) email = this.$emailTracker.getEmail()

        // Pull out the rest of the data
        const { name = null, phone = null, address = {} } = data

        // Format the Addreess
        let street = address.address1 || null
        if (street && address.address2) street += ' ' + address.address2
        const addressObj = address.zipcode
            ? {
                  country: address.country || null,
                  street,
                  state: address.stateId || null,
                  city: address.city || null,
                  postalCode: address.zipcode || null,
              }
            : null
        // Layer In GeoLocationData
        const geoData = this.getGeoLocationData()

        // Leave out any data we don't have
        const identity = {
            isSalesRep: this.isSalesRep(),
            ...(email && { id: email, email }),
            ...(name && { name }),
            ...(phone && { phone }),
            ...(addressObj && { address: addressObj }),
            ...(this.store.state.globalId && { global_id: this.store.state.globalId }),
            ...(this.store.state.permId && { cart_id: this.store.state.permId }),
            ...(geoData && geoData),
        }
        window.analytics.identify(userId, identity)
    }

    async emitIdentifyFitment(fitment) {
        // We do not want to fire an identify event without a UserId.
        // We are no longer supporting Anonymous IDs
        // Per Chris Wall: We are no longer identifying people unless we know them
        const userId = await this.getUserId()
        if (!userId) return

        const geoData = this.getGeoLocationData()
        window.analytics.identify(userId, {
            year: fitment.year,
            make: fitment.make,
            model: fitment.model,
            isSalesRep: this.isSalesRep(),
            ...(this.store.state.globalId && { global_id: this.store.state.globalId }),
            ...(geoData && geoData),
        })
    }

    emitPage(to) {
        const geoData = this.getGeoLocationData()

        window.analytics.page(getPageName(this.$sitewideData.config?.siteName), {
            pageType: getPageType(to.name),
            isSalesRep: this.isSalesRep(),
            ...(geoData && geoData),
        })
        this.tryEmitCart(to.name)
    }

    emitCart() {
        this.track('Cart Viewed', {
            cart_id: this.store.state.permId,
            products: this.getCartProducts(this.store.state.cart.data.items),
        })
    }

    // === HELPERS === //

    tryEmitCart(routeName) {
        // emit cart if it's ready (and the right route)
        if (routeName !== 'cart') return

        // cart is defer loaded, tis not ready yet (really only if cart is your 1st hit)
        if (!this.store.state.cart.data.items) {
            const unwatch = this.store.watch(
                (state) => {
                    return state.cart.data
                },
                () => {
                    if (this.store.state.cart.data.items !== undefined) {
                        this.emitCart()
                        unwatch()
                    }
                }
            )
        } else this.emitCart()
    }

    // === GETTERS === //

    getGeoLocationData() {
        if (!this.$geolocation?.enabled) return null

        return {
            edgePostalCode: this.$geolocation.edgePostalCode,
            userPostalCode: this.$geolocation.userPostalCode,
        }
    }

    getGeoPostalCode() {
        if (!this.$geolocation?.enabled) return null
        return this.$geolocation.zipcode
    }

    estimatedDelivery(shippingData) {
        // Estimated Delivery Details
        let eddMessage = null
        let eddMin = null
        let eddMax = null
        // Early out if shippingData undefined
        if (!shippingData) return { eddMessage, eddMin, eddMax }

        if (shippingData.estimatedDeliveryDate) {
            eddMessage = getEddText(shippingData, true)
            eddMin = shippingData.estimatedDeliveryDate.min
            eddMax = shippingData.estimatedDeliveryDate.max
        }
        return { eddMessage, eddMin, eddMax }
    }

    // Used by Fitment.js and products.js
    getFitment() {
        return {
            year: this.store.state.fitment.data.year,
            make: this.store.state.fitment.data.make,
            model: this.store.state.fitment.data.model,
        }
    }

    // Used by navigation.js
    getFullFitment() {
        return {
            year: this.store.state.fitment.data.year,
            make: this.store.state.fitment.data.make,
            model: this.store.state.fitment.data.model,
            bed: this.store.state.fitment.data.bed,
            body: this.store.state.fitment.data.body,
            engine: this.store.state.fitment.data.engine,
        }
    }

    // Used by emitCart() and checkout
    // loops through an array of cart products and returns them
    getCartProducts(products) {
        return products.map((product, index) => {
            return this.getCartProduct(product, index + 1)
        })
    }

    // Used by getCartProducts() and Extend and Product
    // returns our standard product obj from a cart obj
    getCartProduct(product, position = 1) {
        const isQuickshipable = this.isQuickship(product?.shipping)
        const { eddMessage, eddMin, eddMax } = this.estimatedDelivery(product?.shipping)
        const isEligibleFreeShipping = product?.shipping?.isEligibleFreeShipping !== undefined
        const primaryFitment = this.getFitment()

        if (!product) return {}

        const obj = {
            name: product.productName,
            sku: product.sku,
            position,
            quantity: product.qty,
            category: product.category,
            // brand
            price: product.salePrice / 100,
            product_id: product.productSlug, // cart also has skuSlug, but receipt obj does not :(
            ...(isEligibleFreeShipping && {
                isEligibleFreeShipping: product?.shipping?.isEligibleFreeShipping,
            }),
            ...(isQuickshipable !== null && { quickship: isQuickshipable }),
            ...(eddMessage && { edd_message: eddMessage }),
            ...(eddMin && { edd_min: eddMin }),
            ...(eddMax && { edd_max: eddMax }),
            ...(primaryFitment && primaryFitment),
            ...(product.installation?.subtotal && {
                installation_price: product.installation.subtotal,
            }),
        }

        if (product.productLineSlug)
            obj.url = `https://${this.$sitewideData.domain}/p/${product.productLineSlug}/${product.productSlug}/`

        // If we have a warranty then add it to the obj in the format
        if (product.warranty) {
            const warrantyArray = Array.isArray(product.warranty)
                ? product.warranty
                : [product.warranty]

            obj.warranty = warrantyArray.map((warranty) => ({
                sku: warranty.id,
                price: warranty.price / 100,
            }))
        }

        return obj
    }

    formatProductData(product) {
        const isQuickshipable = this.isQuickship(product.shipping)
        const { eddMessage, eddMin, eddMax } = this.estimatedDelivery(product.shipping)
        const isEligibleFreeShipping = product?.shipping?.isEligibleFreeShipping !== undefined
        const primaryFitment = this.getFitment()

        return {
            ...(product.sku && { sku: product.sku }),
            ...(product.skuSlug && { product_id: product.skuSlug }),
            category: getCategoryTreeFromCrumbs(product.crumbs).slugs.join('/'),
            name: product.name,
            brand: product.brand.name,
            ...(product.actualPrice && { price: product.actualPrice.min / 100 }),
            quantity: 1,
            currency: 'usd',
            ...(product.actualPrice && { value: product.actualPrice.min / 100 }), // Total value of the product after quantity
            ...(isEligibleFreeShipping && {
                isEligibleFreeShipping: product?.shipping?.isEligibleFreeShipping,
            }),
            ...(isQuickshipable && { quickship: isQuickshipable }),
            ...(isQuickshipable !== null && { quickship: isQuickshipable }),
            ...(eddMessage && { edd_message: eddMessage }),
            ...(eddMin && { edd_min: eddMin }),
            ...(eddMax && { edd_max: eddMax }),
            ...(primaryFitment && primaryFitment),
        }
    }

    getAlgoliaQueryInfo() {
        if (!this.$search) return {}
        const { indexName, queryID } = this.$search.catalog.state
        return {
            ...(indexName && { index: indexName }),
            ...(queryID && { queryID }),
        }
    }

    // TODO: We need to do this because back end does not give us ObjectID
    // but it is lame and we need to find a better way eventually.
    async getAlgoliaObjectId({ productLineSlug, skuSlug, fitmentData }) {
        // We must have at least a one of these to even do the search
        if (!productLineSlug && !skuSlug) return null

        let fitmentSlugs
        // Convert fitmentData to the slugified versions
        if (fitmentData) {
            fitmentSlugs = {
                year: fitmentData.year,
                ...(fitmentData.makeName && {
                    makeSlug: this.slugify(fitmentData.makeName),
                }),
                ...(fitmentData.modelName && {
                    modelSlug: this.slugify(fitmentData.modelName),
                }),
                ...(fitmentData.bedName && {
                    bedSlug: this.slugify(fitmentData.bedName),
                }),
                ...(fitmentData.bodyName && {
                    bodySlug: this.slugify(fitmentData.bodyName),
                }),
                ...(fitmentData.engineName && {
                    engineSlug: this.slugify(fitmentData.engineName),
                }),
            }
            // If we don't have fitmentData get what we have in store
        } else fitmentSlugs = this.store.state.fitment.data

        // Start fresh on filters
        this.$search.productsObjectId.clearFilters()
        // Filter by fitment
        setFitmentFilters(this.$search.productsObjectId, fitmentSlugs)
        // Filter by the product
        if (productLineSlug)
            this.$search.productsObjectId.addFilters('productLineSlug', productLineSlug)

        // We need to also lookup by SKU
        if (skuSlug) this.$search.productsObjectId.addFilters('skuSlug', skuSlug)

        // Get he results
        const { results } = await this.$search.productsObjectId._search()
        if (results && results.length > 0) return results[0].objectID

        return null
    }

    // Used by getCartProduct() and products
    isQuickship(shippingData) {
        if (!this.$sitewideData.config.sameDayShippingEnabled || !shippingData) return null
        return !!shippingData.sameDay || shippingData.sameDayShipping
    }

    slugify(string) {
        return string.replace(/["' ]/g, '-').replace('--', '-')
    }
}
